import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { useParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Star, Calendar, Clock, Shield, ThumbsUp, Award, Heart, Calendar as CalendarIcon } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import SoftSkillsBadges, { SoftSkill } from '@/components/SoftSkillsBadges';

interface CaregiverReview {
  id: string;
  author: string;
  date: string;
  rating: number;
  comment: string;
}

interface CaregiverService {
  id: string;
  name: string;
  description: string;
  duration: string;
  price?: string;
}

interface CaregiverData {
  id: string;
  name: string;
  profileImage: string;
  role: string;
  yearsExperience: number;
  bio: string;
  specialties: string[];
  averageRating: number;
  reviewCount: number;
  services: CaregiverService[];
  reviews: CaregiverReview[];
  availability: string[];
  softSkills: SoftSkill[];
  isPremium?: boolean;
  background: {
    verified: boolean;
    education: string;
    certifications: string[];
  };
}

const RatingStars: React.FC<{ rating: number }> = ({ rating }) => {
  return (
    <div className="flex">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-4 w-4 ${
            star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
          }`}
        />
      ))}
    </div>
  );
};

const mockCaregivers: Record<string, CaregiverData> = {
  "cg-123": {
    id: "cg-123",
    name: "Sarah Johnson",
    profileImage: "https://source.unsplash.com/WLUHO9A_xik/400x400",
    role: "Certified Home Health Aide",
    yearsExperience: 5,
    bio: "I've been a caregiver for 5 years, specializing in elder care and those with mobility issues. I strive to provide compassionate support that enhances my clients' quality of life and independence.",
    specialties: ["Elder Care", "Mobility Assistance", "Medication Management"],
    averageRating: 4.8,
    reviewCount: 27,
    services: [
      {
        id: "srv-1",
        name: "Home Care",
        description: "Personalized in-home care services including hygiene assistance, light housekeeping, and companionship.",
        duration: "Flexible (2-8 hours)"
      },
      {
        id: "srv-2",
        name: "Medication Management",
        description: "Ensuring medications are taken correctly and on schedule according to doctor's instructions.",
        duration: "Included with Home Care"
      },
      {
        id: "srv-3",
        name: "Specialized Care",
        description: "Care for those with specific conditions like Alzheimer's, Parkinson's, or post-surgery rehabilitation.",
        duration: "Flexible (2-8 hours)"
      }
    ],
    reviews: [
      {
        id: "rev-1",
        author: "Michael R.",
        date: "March 15, 2023",
        rating: 5,
        comment: "Sarah is amazing with my mother. She's patient, kind and very professional. My mother's mood has improved significantly since Sarah started caring for her."
      },
      {
        id: "rev-2",
        author: "Jennifer T.",
        date: "February 3, 2023",
        rating: 5,
        comment: "We hired Sarah to help my dad after his hip surgery. She was knowledgeable about his recovery needs and made sure he did his exercises daily. Highly recommend!"
      },
      {
        id: "rev-3",
        author: "David L.",
        date: "January 20, 2023",
        rating: 4,
        comment: "Very reliable and communicative. Sarah always lets us know how my aunt is doing and any concerns she notices."
      }
    ],
    availability: ["Weekdays 7AM-7PM", "Weekends 9AM-5PM"],
    softSkills: [
      { name: "Empathy", level: "expert", icon: "hand-heart", description: "Deeply understands and responds to client emotions and needs." },
      { name: "Communication", level: "advanced", icon: "graduation-cap", description: "Clear and effective communication with clients and family members." },
      { name: "Patience", level: "expert", icon: "award", description: "Exceptional ability to remain calm and supportive in challenging situations." },
      { name: "Problem Solving", level: "intermediate", icon: "thumbs-up", description: "Quick to adapt and find solutions to unexpected situations." }
    ],
    isPremium: true,
    background: {
      verified: true,
      education: "Associate's Degree in Health Science",
      certifications: ["Certified Nursing Assistant (CNA)", "CPR/First Aid", "Dementia Care Certified"]
    }
  },
  "cg-456": {
    id: "cg-456",
    name: "Robert Chen",
    profileImage: "https://source.unsplash.com/7YVZYZeITc8/400x400",
    role: "Licensed Practical Nurse",
    yearsExperience: 8,
    bio: "With 8 years of nursing experience, I specialize in providing comprehensive care for seniors and those with chronic conditions. I'm committed to maintaining dignity and independence for all my clients.",
    specialties: ["Chronic Disease Management", "Wound Care", "Rehabilitation Support"],
    averageRating: 4.9,
    reviewCount: 42,
    services: [
      {
        id: "srv-1",
        name: "Skilled Nursing Care",
        description: "Professional nursing services including medication administration, wound care, and vital sign monitoring.",
        duration: "2-4 hours sessions"
      },
      {
        id: "srv-2",
        name: "Post-Hospital Recovery",
        description: "Specialized care following hospital discharge to ensure proper recovery and prevent readmission.",
        duration: "Custom care plans"
      },
      {
        id: "srv-3",
        name: "Chronic Condition Management",
        description: "Ongoing care for conditions like diabetes, COPD, heart disease, with coordination with medical providers.",
        duration: "Regular scheduled visits"
      }
    ],
    reviews: [
      {
        id: "rev-1",
        author: "Patricia M.",
        date: "April 10, 2023",
        rating: 5,
        comment: "Robert is exceptional. His nursing background makes a huge difference in caring for my husband's complex health needs. We've had other caregivers before, but none with his level of expertise."
      },
      {
        id: "rev-2",
        author: "William S.",
        date: "March 22, 2023",
        rating: 5,
        comment: "After my stroke, Robert helped me regain independence through consistent therapy exercises and encouragement. His patience is remarkable."
      },
      {
        id: "rev-3",
        author: "Elizabeth J.",
        date: "February 15, 2023",
        rating: 4,
        comment: "Very thorough and attentive to my mother's changing needs. Robert communicates well with her doctors and keeps detailed notes of her condition."
      }
    ],
    availability: ["Full-time availability", "Overnight care available"],
    softSkills: [
      { name: "Clinical Reasoning", level: "expert", icon: "graduation-cap", description: "Exceptional ability to make sound clinical judgments based on patient conditions." },
      { name: "Leadership", level: "advanced", icon: "award", description: "Effectively coordinates care and guides family caregivers." },
      { name: "Adaptability", level: "advanced", icon: "thumbs-up", description: "Quickly adjusts care approaches based on changing patient needs." },
      { name: "Compassion", level: "expert", icon: "hand-heart", description: "Provides care with genuine empathy and understanding." },
      { name: "Time Management", level: "intermediate", icon: "star", description: "Efficiently balances multiple patient needs and priorities." }
    ],
    isPremium: true,
    background: {
      verified: true,
      education: "Bachelor's of Science in Nursing",
      certifications: ["Licensed Practical Nurse (LPN)", "Advanced Cardiac Life Support", "IV Therapy Certified"]
    }
  }
};

const getInitials = (name: string) => {
  return name
    .split(' ')
    .map(part => part.charAt(0))
    .join('')
    .toUpperCase();
};

const CaregiverProfile: React.FC = () => {
  const { caregiverId = "cg-123" } = useParams();
  const [caregiver, setCaregiver] = useState<CaregiverData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    setTimeout(() => {
      const caregiverData = mockCaregivers[caregiverId] || mockCaregivers["cg-123"];
      setCaregiver(caregiverData);
      setLoading(false);
    }, 600);
  }, [caregiverId]);

  if (loading || !caregiver) {
    return (
      <Layout>
        <div className="pt-32 pb-20 flex justify-center">
          <div className="loader"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>{caregiver.name} | Professional Caregiver | CareConnect</title>
        <meta name="description" content={`View ${caregiver.name}'s profile, reviews, and available services.`} />
      </Helmet>

      <section className="pt-32 pb-20">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="sticky top-24"
              >
                <Card className="shadow-md mb-6">
                  <CardContent className="pt-6">
                    <div className="flex flex-col items-center text-center mb-4">
                      <Avatar className="h-32 w-32 mb-4">
                        <AvatarImage src={caregiver.profileImage} alt={caregiver.name} />
                        <AvatarFallback>{getInitials(caregiver.name)}</AvatarFallback>
                      </Avatar>
                      <h1 className="text-2xl font-bold text-care-800">{caregiver.name}</h1>
                      <p className="text-care-600 mb-2">{caregiver.role}</p>
                      <div className="flex items-center gap-1 mb-2">
                        <RatingStars rating={caregiver.averageRating} />
                        <span className="text-sm font-medium">
                          {caregiver.averageRating} ({caregiver.reviewCount} reviews)
                        </span>
                      </div>
                      <div className="flex gap-1 mt-2">
                        {caregiver.background.verified && (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            <Shield className="h-3 w-3 mr-1" /> Verified
                          </Badge>
                        )}
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          <Award className="h-3 w-3 mr-1" /> {caregiver.yearsExperience} Years Exp
                        </Badge>
                        {caregiver.isPremium && (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                            <Star className="h-3 w-3 mr-1" fill="currentColor" /> Premium
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="border-t border-gray-100 pt-4 mt-4">
                      <h3 className="font-medium mb-2 text-care-700">Specialties</h3>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {caregiver.specialties.map((specialty, index) => (
                          <Badge key={index} variant="secondary" className="bg-care-50 text-care-700 hover:bg-care-100">
                            {specialty}
                          </Badge>
                        ))}
                      </div>
                      
                      <h3 className="font-medium mb-2 text-care-700">Availability</h3>
                      <ul className="space-y-1 mb-4">
                        {caregiver.availability.map((slot, index) => (
                          <li key={index} className="text-sm flex items-start gap-2">
                            <CalendarIcon className="h-4 w-4 text-care-500 mt-0.5" />
                            <span>{slot}</span>
                          </li>
                        ))}
                      </ul>
                      
                      <h3 className="font-medium mb-2 text-care-700">Certifications</h3>
                      <ul className="space-y-1">
                        {caregiver.background.certifications.map((cert, index) => (
                          <li key={index} className="text-sm flex items-start gap-2">
                            <Award className="h-4 w-4 text-care-500 mt-0.5" />
                            <span>{cert}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            <div className="lg:col-span-2 space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Card className="shadow-md">
                  <CardHeader>
                    <CardTitle>About {caregiver.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{caregiver.bio}</p>
                    <div className="mt-4 bg-care-50 p-4 rounded-lg">
                      <p className="text-sm font-medium text-care-700">Education</p>
                      <p className="text-sm text-muted-foreground">{caregiver.background.education}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <Card className="shadow-md">
                  <CardHeader>
                    <CardTitle>Soft Skills</CardTitle>
                    <CardDescription>Professional skills and competencies</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <SoftSkillsBadges skills={caregiver.softSkills} showDetails={true} />
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Card className="shadow-md">
                  <CardHeader>
                    <CardTitle>Services Offered</CardTitle>
                    <CardDescription>Personalized care services available</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {caregiver.services.map((service) => (
                        <div key={service.id} className="border-b border-gray-100 last:border-0 pb-4 last:pb-0">
                          <div className="flex justify-between items-start mb-1">
                            <h3 className="font-medium text-lg text-care-800">{service.name}</h3>
                            <Badge variant="outline" className="bg-care-50 text-care-700">
                              <Clock className="h-3 w-3 mr-1" /> {service.duration}
                            </Badge>
                          </div>
                          <p className="text-muted-foreground text-sm">{service.description}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
              >
                <Card className="shadow-md">
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <CardTitle>Client Reviews</CardTitle>
                      <Badge className="bg-amber-50 text-amber-700 border-0">
                        <ThumbsUp className="h-3 w-3 mr-1" /> {caregiver.reviewCount} Reviews
                      </Badge>
                    </div>
                    <CardDescription>See what clients are saying</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {caregiver.reviews.map((review) => (
                        <div key={review.id} className="border-b border-gray-100 last:border-0 pb-6 last:pb-0">
                          <div className="flex justify-between items-start mb-1">
                            <div>
                              <p className="font-medium text-care-800">{review.author}</p>
                              <p className="text-sm text-muted-foreground">{review.date}</p>
                            </div>
                            <RatingStars rating={review.rating} />
                          </div>
                          <p className="mt-2 text-muted-foreground">"{review.comment}"</p>
                        </div>
                      ))}
                    </div>
                    
                    <Alert className="mt-6 bg-blue-50 border-blue-100">
                      <AlertDescription className="text-blue-700 flex items-center gap-2">
                        <Heart className="h-4 w-4" />
                        All reviews are from verified clients who received care from {caregiver.name}.
                      </AlertDescription>
                    </Alert>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CaregiverProfile;
